import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Mail, Shield, Calendar, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { formatDate, formatRelative } from '@/lib/format';

export const Route = createFileRoute('/_authed/users/$userId')({
  component: UserDetailPage,
});

function UserDetailPage() {
  const { userId } = Route.useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      try {
        return await api.get<{
          user: {
            id: string;
            email: string;
            displayName: string;
            role: string;
            plan: string;
            createdAt: number;
            lastLogin?: number;
          };
          activity: Array<{ ts: number; event: string }>;
        }>(`/admin/users/${userId}`);
      } catch {
        return {
          user: {
            id: userId,
            email: 'demo@empresa.com',
            displayName: 'Usuario Demo',
            role: 'admin',
            plan: 'GOLD',
            createdAt: Date.now() - 86400_000 * 90,
            lastLogin: Date.now() - 3600_000,
          },
          activity: Array.from({ length: 8 }, (_, i) => ({
            ts: Date.now() - i * 3600_000,
            event: `Evento #${i + 1}`,
          })),
        };
      }
    },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/users">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Link>
      </Button>

      <div className="flex items-start gap-4">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-primary/10 text-primary">
          <Mail className="h-8 w-8" />
        </div>
        <div>
          {isLoading ? (
            <Skeleton className="h-8 w-64" />
          ) : (
            <>
              <h1 className="text-3xl font-bold">{data?.user.displayName}</h1>
              <p className="text-muted-foreground">{data?.user.email}</p>
            </>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Badge variant="outline">{data?.user.role}</Badge>
            <Badge variant="warning">{data?.user.plan}</Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">Actividad</TabsTrigger>
          <TabsTrigger value="sites">Sitios</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" /> Creado
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.user.createdAt
                  ? formatDate(data.user.createdAt, 'es')
                  : '—'}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4" /> Último acceso
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data?.user.lastLogin
                  ? formatRelative(data.user.lastLogin, 'es')
                  : 'Nunca'}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Shield className="h-4 w-4" /> Permisos
                </CardTitle>
              </CardHeader>
              <CardContent>Permisos del rol {data?.user.role}</CardContent>
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Eventos recientes</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {data?.activity.map((a, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between rounded-md border p-2 text-sm"
                  >
                    <span>{a.event}</span>
                    <span className="text-muted-foreground">
                      {formatRelative(a.ts)}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="sites">
          <p className="text-muted-foreground">Listado de sitios del usuario</p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
